import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

// Types
export interface Project {
  id: number;
  title: string;
  category: string;
  location: string;
  year: string;
  client: string;
  image_url: string;
  gallery_images?: string[];
  description: string;
  services?: string[];
  status: string;
}

export interface Message {
  id: number;
  name: string;
  email: string;
  phone?: string;
  service: string;
  message: string;
  status: string;
  read: boolean;
  created_at: string;
}

export interface Review {
  id: number;
  name: string;
  email: string;
  rating: number;
  comment: string;
  status: string;
  source: string;
  created_at: string;
}

export interface EmailAttachment {
  id?: string;
  filename: string;
  content_type?: string;
  size?: number;
  content?: string;
}

export interface Email {
  id: number;
  from_name: string;
  from_email: string;
  to_email: string;
  subject: string;
  body: string;
  html_body?: string;
  folder: string;
  starred: boolean;
  read: boolean;
  inbound?: boolean;
  message_id?: string;
  resend_id?: string;
  attachments?: EmailAttachment[];
  created_at: string;
}

export interface EmailIdentity {
  id: number;
  email: string;
  name: string;
  is_default: boolean;
  created_at: string;
}

export interface SiteSettings {
  siteName: string;
  contactEmail: string;
  contactPhone: string;
  officeAddress: string;
}

interface CMSContextType {
  projects: Project[];
  messages: Message[];
  reviews: Review[];
  emails: Email[];
  settings: SiteSettings;
  isLoading: boolean;
  
  // Project actions
  getProjectById: (id: string) => Project | undefined;
  addProject: (project: Omit<Project, 'id'>) => Promise<void>;
  updateProject: (id: number, project: Partial<Project>) => Promise<void>;
  deleteProject: (id: number) => Promise<void>;
  refreshProjects: () => Promise<void>;
  
  // Message actions
  addMessage: (message: Omit<Message, 'id' | 'created_at'>) => Promise<void>;
  updateMessageStatus: (id: number, status: string) => Promise<void>;
  deleteMessage: (id: number) => Promise<void>;
  refreshMessages: () => Promise<void>;
  
  // Review actions
  addReview: (review: Omit<Review, 'id' | 'created_at' | 'status' | 'source'> & { source?: string }) => Promise<{ success: boolean; message: string }>;
  refreshReviews: () => Promise<void>;
  
  // Email actions
  refreshEmails: (folder?: string) => Promise<void>;
  addEmail: (email: Omit<Email, 'id' | 'created_at'> & { send?: boolean; identity_id?: number }) => Promise<Email>;
  updateEmail: (id: number, email: Partial<Email>) => Promise<void>;
  deleteEmail: (id: number) => Promise<void>;

  // Email identity actions
  emailIdentities: EmailIdentity[];
  refreshEmailIdentities: () => Promise<void>;
  addEmailIdentity: (identity: Omit<EmailIdentity, 'id' | 'created_at'>) => Promise<EmailIdentity>;
  updateEmailIdentity: (id: number, identity: Partial<EmailIdentity>) => Promise<void>;
  deleteEmailIdentity: (id: number) => Promise<void>;
  
  // Settings actions
  updateSettings: (settings: Partial<SiteSettings>) => Promise<void>;
}

const defaultSettings: SiteSettings = {
  siteName: 'Bomtrack Nigeria Limited',
  contactEmail: 'info@bomtrackng.com',
  contactPhone: '+234 803 740 0677',
  officeAddress: 'Shop D-203, 2nd Floor, Left Wing, Awoyaya Complex, Awoyaya, Ibeju-Lekki, Lagos State, Nigeria',
};

const CMSContext = createContext<CMSContextType | undefined>(undefined);

const API_BASE = '/api';

export function CMSProvider({ children }: { children: ReactNode }) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [emails, setEmails] = useState<Email[]>([]);
  const [emailIdentities, setEmailIdentities] = useState<EmailIdentity[]>([]);
  const [settings, setSettings] = useState<SiteSettings>(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);

  // Load initial data
  useEffect(() => {
    refreshProjects();
    refreshMessages();
    refreshReviews();
    refreshEmails();
    refreshEmailIdentities();
  }, []);

  const refreshProjects = async () => {
    try {
      const response = await fetch(`${API_BASE}/projects`, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` } });
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setProjects(data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshMessages = async () => {
    try {
      const response = await fetch(`${API_BASE}/messages`, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` } });
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setMessages(data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    }
  };

  const refreshReviews = async () => {
    try {
      const response = await fetch(`${API_BASE}/reviews`, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` } });
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setReviews(data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    }
  };

  const refreshEmails = async (folder?: string) => {
    try {
      const url = folder ? `${API_BASE}/email?folder=${folder}` : `${API_BASE}/email`;
      const response = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` } });
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setEmails(data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch emails:', error);
    }
  };

  const refreshEmailIdentities = async () => {
    try {
      const response = await fetch(`${API_BASE}/email/identities`, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` } });
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setEmailIdentities(data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch email identities:', error);
    }
  };

  const addEmailIdentity = async (identity: Omit<EmailIdentity, 'id' | 'created_at'>) => {
    const response = await fetch(`${API_BASE}/email/identities`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` },
      body: JSON.stringify(identity),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Failed to save identity' }));
      throw new Error(err.error || 'Failed to save identity');
    }
    const data = await response.json();
    await refreshEmailIdentities();
    return data;
  };

  const updateEmailIdentity = async (id: number, identity: Partial<EmailIdentity>) => {
    const response = await fetch(`${API_BASE}/email/identities/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` },
      body: JSON.stringify(identity),
    });
    if (!response.ok) throw new Error('Failed to update identity');
    await refreshEmailIdentities();
  };

  const deleteEmailIdentity = async (id: number) => {
    const response = await fetch(`${API_BASE}/email/identities/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` },
    });
    if (!response.ok) throw new Error('Failed to delete identity');
    await refreshEmailIdentities();
  };

  const addEmail = async (email: Omit<Email, 'id' | 'created_at'> & { send?: boolean; identity_id?: number }) => {
    const response = await fetch(`${API_BASE}/email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` },
      body: JSON.stringify(email),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Failed to save email' }));
      throw new Error(err.error || 'Failed to save email');
    }
    const data = await response.json();
    await refreshEmails();
    return data;
  };

  const updateEmail = async (id: number, email: Partial<Email>) => {
    const response = await fetch(`${API_BASE}/email/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` },
      body: JSON.stringify(email),
    });
    if (!response.ok) throw new Error('Failed to update email');
    await refreshEmails();
  };

  const deleteEmail = async (id: number) => {
    const response = await fetch(`${API_BASE}/email/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` },
    });
    if (!response.ok) throw new Error('Failed to delete email');
    await refreshEmails();
  };

  const getProjectById = (id: string) => {
    return projects.find(p => p.id === parseInt(id));
  };

  const addProject = async (project: Omit<Project, 'id'>) => {
    const response = await fetch(`${API_BASE}/projects`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` },
      body: JSON.stringify(project),
    });
    if (!response.ok) throw new Error('Failed to add project');
    await refreshProjects();
  };

  const updateProject = async (id: number, project: Partial<Project>) => {
    const response = await fetch(`${API_BASE}/projects/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` },
      body: JSON.stringify(project),
    });
    if (!response.ok) throw new Error('Failed to update project');
    await refreshProjects();
  };

  const deleteProject = async (id: number) => {
    const response = await fetch(`${API_BASE}/projects/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` },
    });
    if (!response.ok) throw new Error('Failed to delete project');
    await refreshProjects();
  };

  const addMessage = async (message: Omit<Message, 'id' | 'created_at'>) => {
    const response = await fetch(`${API_BASE}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` },
      body: JSON.stringify(message),
    });
    if (!response.ok) throw new Error('Failed to send message');
    await refreshMessages();
  };

  const updateMessageStatus = async (id: number, status: string) => {
    const response = await fetch(`${API_BASE}/messages/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` },
      body: JSON.stringify({ status }),
    });
    if (!response.ok) throw new Error('Failed to update message');
    await refreshMessages();
  };

  const deleteMessage = async (id: number) => {
    const response = await fetch(`${API_BASE}/messages/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` },
    });
    if (!response.ok) throw new Error('Failed to delete message');
    await refreshMessages();
  };

  const addReview = async (review: Omit<Review, 'id' | 'created_at' | 'status' | 'source'> & { source?: string }) => {
    const response = await fetch(`${API_BASE}/reviews`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` },
      body: JSON.stringify(review),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to submit review');
    await refreshReviews();
    return data;
  };

  const updateSettings = async (newSettings: Partial<SiteSettings>) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
  };

  return (
    <CMSContext.Provider
      value={{
        projects,
        messages,
        reviews,
        emails,
        settings,
        isLoading,
        getProjectById,
        addProject,
        updateProject,
        deleteProject,
        refreshProjects,
        addMessage,
        updateMessageStatus,
        deleteMessage,
        refreshMessages,
        addReview,
        refreshReviews,
        refreshEmails,
        addEmail,
        updateEmail,
        deleteEmail,
        emailIdentities,
        refreshEmailIdentities,
        addEmailIdentity,
        updateEmailIdentity,
        deleteEmailIdentity,
        updateSettings,
      }}
    >
      {children}
    </CMSContext.Provider>
  );
}

export function useCMS() {
  const context = useContext(CMSContext);
  if (context === undefined) {
    throw new Error('useCMS must be used within a CMSProvider');
  }
  return context;
}
